/* eslint-disable @typescript-eslint/no-this-alias */
import {GraphHelperService} from '../../helpers/graph-helper.service';
import {Injectable} from '@angular/core';
import {ConfigService} from '@q9elements/ui-kit/common';
import {StylesService} from '../../styles/styles.service';
import {EditorService} from './editor.service';
import {ContextMenuService} from '../../../diagram-interaction/context-menu/context-menu.service';
import {GraphActionsService} from '../../../diagram-interaction/actions/graph-actions.service';
import {FlowlineHelperService} from '../../helpers/flowline-helper.service';
import {isActivity, isConnector, isTerminator} from '../../helpers/shapes-detectors.util';
import {FlowlineCommands} from '../../../commands/flowline/flowline.commands';
import {UndoService} from '../../../commands/undo.service';
import {ShapesTemplateService} from '../../shapes/shapes-template.service';
import {GraphEventsHandlingService} from '../../../diagram-interaction/graph-events-handler/graph-events-handling.service';
import {GraphExportService} from '../../../diagram-interaction/services/graph-export.service';
import {SidebarService} from '../../../editor-interface/sidebar/sidebar.service';
import {ModeManagerService} from '../../../editor-state/mode-manager.service';
import {ClientEnvService} from '../../../core/services/client-env.service';
import {DiagramJsonHandlerService} from '../../../diagram-data/diagram-json-handler.service';
import {CANVAS_TOP_MARGIN} from 'src/app/editor-interface/toolbar/util/constants';
import {SfaHeaderService} from '../../sfa-header/sfa-header.service';
import {DiagramTypeConfigService} from '../../../core/services/diagram-type-config.service';
import {FlowlineFormUtil} from '../../helpers/flowline-form.util';

declare let mxClient: any;
declare let mxConstants: any;
declare let mxCellState: any;
declare let mxEventSource: any;
declare let mxEdgeStyle: any;
declare let mxImage: any;
declare let mxEventObject: any;
declare let mxShape: any;
declare let mxUtils: any;
declare let mxResources: any;
declare let mxGraphView: any;
declare let mxRectangle: any;
declare let mxGraphHandler: any;
declare let mxImageShape: any;
declare let mxConnectionHandler: any;
declare let mxEvent: any;
declare let mxMouseEvent: any;
declare let mxPolyline: any;
declare let mxEdgeHandler: any;
declare let mxMorphing: any;
declare let mxPoint: any;
declare let mxPopupMenu: any;
declare let mxVertexHandler: any;
declare let mxConstraintHandler: any;
declare let mxPanningHandler: any;
declare let mxGraphModel: any;
declare let mxKeyHandler: any;
declare let mxGeometry: any;
declare let mxLog: any;

@Injectable()
export class EditorUiService {
  editorUI;

  constructor(
    q9Editor: EditorService,
    contextMenuSvc: ContextMenuService,
    graphActions: GraphActionsService,
    flowlineHelperSvc: FlowlineHelperService,
    styles: StylesService,
    graphHelperSvc: GraphHelperService,
    flowlineCommands: FlowlineCommands,
    undoSvc: UndoService,
    templateSvc: ShapesTemplateService,
    graphEventHandlingSvc: GraphEventsHandlingService,
    graphExportSvc: GraphExportService,
    modeManagerSvc: ModeManagerService,
    clientEnvSvc: ClientEnvService,
    sidebarSvc: SidebarService,
    jsonHandlerSvc: DiagramJsonHandlerService,
    sfaHeaderSvc: SfaHeaderService,
    diagramTypeConfigService: DiagramTypeConfigService
  ) {
    const IMAGE_PATH = 'images';

    /** --- END ---*/

    const EditorUI = function (editor, container) {
      mxEventSource.call(this);

      this.editor = editor || new q9Editor.q9Editor();
      //noinspection JSAnnotator
      this.container = container || document.body;
      const graph = this.editor.graph;
      // Sets the default edge style
      let currentEdgeStyle = styles.flowline().mxCellCssAsObject();
      let currentStyle = {};

      this.clearDefaultStyle = function () {
        currentEdgeStyle = styles.flowline().mxCellCssAsObject();
        currentStyle = {};
      };

      // Set 'CTRL/CMD + P' combination to call printing
      document.body.addEventListener('keydown', function (event) {
        if (event.keyCode === 80 && graph.isToggleEvent(event)) {
          event.preventDefault();
          graphExportSvc.printPrepare();
        }
      });

      document.body.addEventListener(
        'wheel',
        function (event) {
          if (event.ctrlKey) {
            event.preventDefault();

            if (event.deltaY < 0) {
              graph.zoomToPointer(graph.customZoomFactor, event);
            } else {
              graph.zoomToPointer(1 / graph.customZoomFactor, event);
            }
          }
        },
        {passive: false}
      );

      document.body.addEventListener('keydown', function (event) {
        if (event.keyCode === 69 && graph.isToggleEvent(event) && !clientEnvSvc.isStripped) {
          event.preventDefault();
          modeManagerSvc.toggle();
        }
      });

      // Pre-fetches submenu image or replaces with embedded image if supported
      if (mxClient.IS_SVG) {
        mxPopupMenu.prototype.submenuImage = `${ConfigService.environment.STATIC_FILES_URL}/icons/action_icons/arrow_right.svg`;
      } else {
        new Image().src = mxPopupMenu.prototype.submenuImage;
      }

      // Pre-fetches connect image
      if (!mxClient.IS_SVG && mxConnectionHandler.prototype.connectImage != null) {
        new Image().src = mxConnectionHandler.prototype.connectImage.src;
      }

      // Creates the user interface
      this.actions = graphActions.init(this);
      this.createDivs();
      this.createUi();
      this.refresh();

      // Disables HTML and text selection
      const textEditing = mxUtils.bind(this, function (evt) {
        if (evt == null) {
          evt = window.event;
        }

        if (this.isSelectionAllowed(evt)) {
          return true;
        }

        return graph.isEditing();
      });

      // Disables text selection while not editing and no dialog visible
      if (this.container == document.body) {
        this.diagramContainer.onselectstart = textEditing;
        this.diagramContainer.onmousedown = textEditing;
      }

      // And uses built-in context menu while editing
      if (mxClient.IS_IE) {
        mxEvent.addListener(this.diagramContainer, 'contextmenu', textEditing);
      } else {
        // Allows browser context menu outside of diagram-render and sidebar
        this.diagramContainer.oncontextmenu = textEditing;
      }

      // Contains the main graph instance inside the given panel
      graph.init(this.diagramContainer);
      graph.setHtmlLabels(true);

      graph.getModel().addListener(mxEvent.CHANGE, (sender, evt) => {
        graphEventHandlingSvc.handle(graph, evt, sender);
      });

      const cellEditorIsStopEditingEvent = graph.cellEditor.isStopEditingEvent;

      graph.cellEditor.isStopEditingEvent = function (evt) {
        return (
          cellEditorIsStopEditingEvent.apply(this, arguments) ||
          (evt.keyCode == 13 && mxEvent.isControlDown(evt))
        );
      };

      // Enables scrollbars and sets cursor style for the container
      graph.container.setAttribute('tabindex', '0');
      graph.container.style.cursor = 'default';
      graph.container.style.backgroundImage = 'url(' + editor.gridImage + ')';
      graph.container.style.backgroundPosition = '-1px -1px';

      //noinspection TypeScriptUnresolvedVariable
      const noBackground =
        mxClient.IS_IE && document['documentMode'] >= 9
          ? 'url(' + this.editor.transparentImage + ')'
          : 'none';

      graph.container.style.backgroundImage = noBackground;
      const bgImg =
        !graph.pageVisible && graph.isGridEnabled()
          ? 'url(' + this.editor.gridImage + ')'
          : noBackground;

      if (graph.view.canvas.ownerSVGElement != null) {
        graph.view.canvas.ownerSVGElement.style.backgroundImage = bgImg;
      } else {
        graph.view.canvas.style.backgroundImage = bgImg;
      }

      graph.container.focus();

      // Keeps graph container focused on mouse down
      const graphFireMouseEvent = graph.fireMouseEvent;

      graph.fireMouseEvent = function (evtName, me, sender) {
        if (evtName == mxEvent.MOUSE_DOWN) {
          this.container.focus();
        }

        graphFireMouseEvent.apply(this, arguments);
      };

      // Configures automatic expand on mouseover
      graph.popupMenuHandler.autoExpand = true;

      // Installs context menu
      graph.popupMenuHandler.factoryMethod = mxUtils.bind(
        this,
        function (menu, cell, evt, special) {
          contextMenuSvc.build(menu, cell, evt, special);
        }
      );

      // Hides context menu
      mxEvent.addGestureListeners(
        document,
        mxUtils.bind(this, function (evt) {
          graph.popupMenuHandler.hideMenu();
        })
      );

      // Create handler for key events
      this.keyHandler = this.createKeyHandler(editor);

      mxEvent.isPopupTrigger = function (evt) {
        return mxEvent.isRightMouseButton(evt);
      };

      // Stores the current style and assigns it to new cells
      const stylesArr = ['shadow', 'glass', 'dashed', 'dashPattern'];
      const connectStyles = ['shape', 'edgeStyle', 'curved', 'rounded', 'elbow'];

      // Constructs the style for the initial edge type defined in the initial value for the currentEdgeStyle
      // This function is overridden below as soon as any style is set in the app.
      let initialEdgeCellStyle = '';

      for (const key in currentEdgeStyle) {
        initialEdgeCellStyle += key + '=' + currentEdgeStyle[key] + ';';
      }

      // Uses the default edge style for connect preview
      graph.connectionHandler.createEdgeState = function (me) {
        const edge = graph.createEdge(null, null, null, null, null, initialEdgeCellStyle);

        return new mxCellState(graph.view, edge, graph.getCellStyle(edge));
      };

      // Keys that should be ignored if the cell has a value (known: new default for all cells is html=1 so
      // for the html key this effecticely only works for edges inserted via the connection handler)
      const valueStyles = ['fontFamily', 'fontSize', 'fontColor', 'fontStyle'];

      // Keys that always update the current edge style regardless of selection
      const alwaysEdgeStyles = [
        'edgeStyle',
        'startArrow',
        'startFill',
        'startSize',
        'endArrow',
        'endFill',
        'endSize'
      ];

      // Keys that are ignored together (if one appears all are ignored)
      const keyGroups = [
        ['startArrow', 'startFill', 'startSize', 'endArrow', 'endFill', 'endSize'],
        ['strokeColor', 'strokeWidth'],
        ['fillColor', 'gradientColor'],
        valueStyles,
        ['align'],
        ['html']
      ];

      // Adds all keys used above to the styles array
      for (let i = 0; i < keyGroups.length; i++) {
        for (let j = 0; j < keyGroups[i].length; j++) {
          stylesArr.push(keyGroups[i][j]);
        }
      }

      for (let i = 0; i < connectStyles.length; i++) {
        stylesArr.push(connectStyles[i]);
      }

      // Implements a global current style for edges and vertices that is applied to new cells
      const insertHandler = function (cells) {
        graph.getModel().beginUpdate();

        try {
          for (let i = 0; i < cells.length; i++) {
            const cell = cells[i];

            // Removes styles defined in the cell style from the styles to be applied
            const cellStyle = graph.getModel().getStyle(cell);
            const tokens = cellStyle != null ? cellStyle.split(';') : [];
            const appliedStyles = stylesArr.slice();

            for (let j = 0; j < tokens.length; j++) {
              const tmp = tokens[j];
              const pos = tmp.indexOf('=');

              if (pos >= 0) {
                const key = tmp.substring(0, pos);
                const index = mxUtils.indexOf(appliedStyles, key);

                if (index >= 0) {
                  appliedStyles.splice(index, 1);
                }

                // Handles special cases where one defined style ignores other styles
                for (let k = 0; k < keyGroups.length; k++) {
                  const group = keyGroups[k];

                  if (mxUtils.indexOf(group, key) >= 0) {
                    for (let l = 0; l < group.length; l++) {
                      const index2 = mxUtils.indexOf(appliedStyles, group[l]);

                      if (index2 >= 0) {
                        appliedStyles.splice(index2, 1);
                      }
                    }
                  }
                }
              }
            }

            // Applies the current style to the cell
            const value = graph.convertValueToString(cell);
            const edge = graph.getModel().isEdge(cell);
            const current = edge ? currentEdgeStyle : currentStyle;

            for (let j = 0; j < appliedStyles.length; j++) {
              const key = appliedStyles[j];
              const styleValue = current[key];

              if (styleValue != null) {
                // Special case: Connect styles are not applied here but in the connection handler
                if (!edge || mxUtils.indexOf(connectStyles, key) < 0) {
                  graph.setCellStyles(key, styleValue, [cell]);
                }
              }
            }
          }
        } finally {
          graph.getModel().endUpdate();
        }
      };

      // graph.addListener('cellsInserted', function (sender, evt) {
      //   insertHandler(evt.getProperty('cells'));
      //   console.log('cellsInserted')
      // });

      /**
       * Function: connect
       *
       * Connects the given source and target using a new edge. This
       * implementation uses <createEdge> to create the edge.
       *
       * Parameters:
       *
       * source - <mxCell> that represents the source terminal.
       * target - <mxCell> that represents the target terminal.
       * evt - Mousedown event of the connect gesture.
       * dropTarget - <mxCell> that represents the cell under the mouse when it was
       * released.
       */
      mxConnectionHandler.prototype.connect = function (source, target, evt, dropTarget) {
        if (target != null || this.isCreateTarget() || this.graph.allowDanglingEdges) {
          // Uses the common parent of source and target or
          // the default parent to insert the edge
          const model = this.graph.getModel();
          let terminalInserted = false;
          let edge = null;

          model.beginUpdate();

          try {
            if (source != null && target == null && this.isCreateTarget()) {
              target = this.createTargetVertex(evt, source);

              if (target != null) {
                dropTarget = this.graph.getDropTarget([target], evt, dropTarget);
                terminalInserted = true;

                // Disables edges as drop targets if the target cell was created
                // FIXME: Should not shift if vertex was aligned (same in Java)
                if (dropTarget == null || !this.graph.getModel().isEdge(dropTarget)) {
                  const pstate = this.graph.getView().getState(dropTarget);

                  if (pstate != null) {
                    const tmp = model.getGeometry(target);

                    tmp.x -= pstate.origin.x;
                    tmp.y -= pstate.origin.y;
                  }
                } else {
                  dropTarget = this.graph.getDefaultParent();
                }

                this.graph.addCell(target, dropTarget);
              }
            }

            let parent = this.graph.getDefaultParent();

            if (
              source != null &&
              target != null &&
              model.getParent(source) == model.getParent(target) &&
              model.getParent(model.getParent(source)) != model.getRoot()
            ) {
              parent = model.getParent(source);

              if (
                source.geometry != null &&
                source.geometry.relative &&
                target.geometry != null &&
                target.geometry.relative
              ) {
                parent = model.getParent(parent);
              }
            }

            // Uses the value of the preview edge state for inserting
            // the new edge into the graph
            let value = null;
            let style = null;

            if (this.edgeState != null) {
              value = this.edgeState.cell.value;
              style = this.edgeState.cell.style;
            }

            edge = this.insertEdge(this.graph.model.getCell(3), null, value, source, target, style);

            if (edge != null) {
              // Updates the connection constraints
              this.graph.setConnectionConstraint(edge, source, true, this.sourceConstraint);
              this.graph.setConnectionConstraint(
                edge,
                target,
                false,
                this.constraintHandler.currentConstraint
              );

              // Uses geometry of the preview edge state
              if (this.edgeState != null) {
                model.setGeometry(edge, this.edgeState.cell.geometry);
              }

              // Makes sure the edge has a non-null, relative geometry
              let geo = model.getGeometry(edge);

              if (geo == null) {
                geo = new mxGeometry();
                geo.relative = true;

                model.setGeometry(edge, geo);
              }

              // Uses scaled waypoints in geometry
              if (this.waypoints != null && this.waypoints.length > 0) {
                const s = this.graph.view.scale;
                const tr = this.graph.view.translate;

                geo.points = [];

                for (let i = 0; i < this.waypoints.length; i++) {
                  const pt = this.waypoints[i];

                  geo.points.push(new mxPoint(pt.x / s - tr.x, pt.y / s - tr.y));
                }
              }

              if (target == null) {
                const t = this.graph.view.translate;
                const s = this.graph.view.scale;
                const pt = new mxPoint(
                  this.currentPoint.x / s - t.x,
                  this.currentPoint.y / s - t.y
                );

                pt.x -= this.graph.panDx / this.graph.view.scale;
                pt.y -= this.graph.panDy / this.graph.view.scale;
                geo.setTerminalPoint(pt, false);
              }

              this.fireEvent(
                new mxEventObject(
                  mxEvent.CONNECT,
                  'cell',
                  edge,
                  'terminal',
                  target,
                  'event',
                  evt,
                  'target',
                  dropTarget,
                  'terminalInserted',
                  terminalInserted
                )
              );
            }
          } catch (e) {
            mxLog.show();
            mxLog.debug(e.message);
          } finally {
            model.endUpdate();
          }

          if (this.select) {
            this.selectCells(edge, terminalInserted ? target : null);
          }
        }
      };

      graph.connectionHandler.addListener(mxEvent.CONNECT, function (sender, evt) {
        const cells = [evt.getProperty('cell')];
        const ctrlPressed = graph.isToggleEvent(evt.properties.event);
        const mapType = jsonHandlerSvc.getMapType();

        // HANDLE Ctrl press
        for (let i = cells.length; i--; ) {
          // Swap source and target if Ctrl key pressed and source or target is missing
          if (ctrlPressed && (!cells[i].source || !cells[i].target)) {
            const model = this.graph.getModel();
            const initialStyle = this.graph.getCellStyle(cells[i]);
            let styleString = cells[i].style;
            const temp = {
              source: cells[i].source,
              exitX: initialStyle.exitX,
              exitY: initialStyle.exitY,
              entryX: initialStyle.entryX,
              entryY: initialStyle.entryY
            };

            cells[i].source = cells[i].target;
            cells[i].target = temp.source;
            styleString = mxUtils.setStyle(styleString, 'entryX', temp.exitX);
            styleString = mxUtils.setStyle(styleString, 'entryY', temp.exitY);
            styleString = mxUtils.setStyle(styleString, 'exitX', temp.entryX);
            styleString = mxUtils.setStyle(styleString, 'exitY', temp.entryY);
            model.setStyle(cells[i], styleString);

            // Swap point positions
            if (cells[i].source) {
              cells[i].geometry.targetPoint = cells[i].geometry.sourcePoint;
              cells[i].geometry.sourcePoint = null;
            } else {
              cells[i].geometry.sourcePoint = cells[i].geometry.targetPoint;
              cells[i].geometry.targetPoint = null;
            }
          }

          //
          const {offset, y} = diagramTypeConfigService.flowlineSettings().geo();

          cells[i].skipUndo = true;
          cells[i].geometry.offset = offset;
          cells[i].geometry.y = y;
          // // set connection points
          flowlineHelperSvc.setConnectionPoints(cells[i]);
          //
          const exitX = this.graph.getCellStyle(cells[i]).exitX;
          const exitY = this.graph.getCellStyle(cells[i]).exitY;
          const entryX = this.graph.getCellStyle(cells[i]).entryX;
          const entryY = this.graph.getCellStyle(cells[i]).entryY;

          //
          if (
            !isNaN(exitX) &&
            !isNaN(exitY) &&
            exitX !== 0 &&
            exitY !== 0 &&
            exitX != 1 &&
            exitY != 1
          ) {
            flowlineHelperSvc.adjustInteriorTerminalPoint(cells[i], exitX, exitY, true);
          }

          if (
            !isNaN(entryX) &&
            !isNaN(entryY) &&
            entryX !== 0 &&
            entryY !== 0 &&
            entryX != 1 &&
            entryY != 1
          ) {
            flowlineHelperSvc.adjustInteriorTerminalPoint(cells[i], entryX, entryY);
          }

          //
          this.graph.selectionModel.clear();
          this.graph.selectionModel.addCell(cells[i]);
          //
          cells[i].setValue(templateSvc.flowline());
          const {start: flowlineStart, end: flowlineEnd} =
            diagramTypeConfigService.flowlineSettings().form;

          currentEdgeStyle.startArrow =
            FlowlineFormUtil.get.start.byTitle(flowlineStart).props[0].val;
          currentEdgeStyle.endArrow = FlowlineFormUtil.get.end.byTitle(flowlineEnd).props[0].val;
          cells[i].setParent(graph.model.getCell(3));
          // //this.graph.popupMenuHandler.factoryMethod.call(editorUi, this.graph.popupMenuHandler, cells[i], { evt: {} }, true);
          // let cell = cells[i];
          // // window.setTimeout(function (cell) {
          // // return function() {
          // // cell.setValue(defaultFlowlineTemplate);
          //
          // //var number = getAvailableNumber(true);
          // //setActivityNumber(cell, number);
          //
          //
          const waypoints = null;
          let srcType = 'none',
            tgtType = 'none';
          const source = cells[i].source;
          const target = cells[i].target;
          let srcId = null,
            tgtId = null;
          // let srcNumber = -1, tgtNumber = -1;
          let srcPosition = null,
            tgtPosition = null;

          //
          if (source) {
            if (isActivity(source)) {
              srcType = 'activity';
            } else if (isConnector(source)) {
              srcType = 'connector';
            } else if (isTerminator(source)) {
              srcType = 'terminator';
            }

            srcId = +source.id;
            // srcNumber = graphHelperSvc.getCellMetadata(source, 'number');
          }

          if (target) {
            if (isActivity(target)) {
              tgtType = 'activity';
            } else if (isConnector(target)) {
              tgtType = 'connector';
            } else if (isTerminator(target)) {
              tgtType = 'terminator';
            }

            tgtId = +target.id;
            // tgtNumber = graphHelperSvc.getCellMetadata(target, 'number');
          }

          //
          const geometry = cells[i].geometry;

          if (geometry.sourcePoint) {
            srcPosition = {x: geometry.sourcePoint.x, y: geometry.sourcePoint.y};
          }

          if (geometry.targetPoint) {
            tgtPosition = {x: geometry.targetPoint.x, y: geometry.targetPoint.y};
          }

          const text = graph.getEditingValue(cells[i]);
          const labelPosition = {
            x: geometry.x,
            y: geometry.y,
            offset: {
              x: geometry.offset.x,
              y: geometry.offset.y
            },
            connectionPoints: {}
          };

          //
          if (!isNaN(exitX)) {
            labelPosition.connectionPoints['exitX'] = exitX;
          }

          if (!isNaN(exitY)) {
            labelPosition.connectionPoints['exitY'] = exitY;
          }

          if (!isNaN(entryX)) {
            labelPosition.connectionPoints['entryX'] = entryX;
          }

          if (!isNaN(entryY)) {
            labelPosition.connectionPoints['entryY'] = entryY;
          }

          //
          const data = {
            mxKey: cells[i].id,
            text: text,
            srcType: srcType,
            tgtType: tgtType,
            waypoints: waypoints,
            srcPosition: srcPosition,
            tgtPosition: tgtPosition,
            position: labelPosition
          };

          if (srcType !== 'none') {
            data['srcId'] = srcId;
          }

          if (tgtType !== 'none') {
            data['tgtId'] = tgtId;
          }

          const createCommand = flowlineCommands.create(data, true);

          undoSvc.executeCommand(createCommand);
          this.graph.popupMenuHandler.popup(
            evt.properties.event.clientX,
            evt.properties.event.clientY,
            cells[i],
            {evt: {}},
            true
          );
        }

        if (evt.getProperty('terminalInserted')) {
          cells.push(evt.getProperty('terminal'));
        }

        insertHandler(cells);
      });

      // This is used below and in Sidebar.dropAndConnect
      this.createCurrentEdgeStyle = function () {
        let style = 'edgeStyle=' + (currentEdgeStyle['edgeStyle'] || 'none') + ';';

        if (currentEdgeStyle['shape'] != null) {
          style += 'shape=' + currentEdgeStyle['shape'] + ';';
        }

        if (currentEdgeStyle['curved'] != null) {
          style += 'curved=' + currentEdgeStyle['curved'] + ';';
        }

        if (currentEdgeStyle['rounded'] != null) {
          style += 'rounded=' + currentEdgeStyle['rounded'] + ';';
        }

        // Special logic for custom property of elbowEdgeStyle
        if (
          currentEdgeStyle['edgeStyle'] == 'elbowEdgeStyle' &&
          currentEdgeStyle['elbow'] != null
        ) {
          style += 'elbow=' + currentEdgeStyle['elbow'] + ';';
        }

        if (currentEdgeStyle['html'] != null) {
          style += 'html=' + currentEdgeStyle['html'] + ';';
        } else {
          style += 'html=1;';
        }

        return style;
      };

      // Uses current edge style for connect preview
      // NOTE: Do not use "this" in here as it points to the UI
      graph.connectionHandler.createEdgeState = mxUtils.bind(this, function (me) {
        const style = this.createCurrentEdgeStyle();
        const edge = graph.createEdge(null, null, null, null, null, style);

        return new mxCellState(graph.view, edge, graph.getCellStyle(edge));
      });

      this.addListener(
        'styleChanged',
        mxUtils.bind(this, function (sender, evt) {
          // Checks if edges and/or vertices were modified
          const cells = evt.getProperty('cells');
          let vertex = false;
          let edge = false;

          if (cells.length > 0) {
            for (let i = 0; i < cells.length; i++) {
              vertex = graph.getModel().isVertex(cells[i]) || vertex;
              edge = graph.getModel().isEdge(cells[i]) || edge;

              if (edge && vertex) {
                break;
              }
            }
          } else {
            vertex = true;
            edge = true;
          }

          const keys = evt.getProperty('keys');
          const values = evt.getProperty('values');

          for (let i = 0; i < keys.length; i++) {
            const common = mxUtils.indexOf(valueStyles, keys[i]) >= 0;

            // Special case: Edge style and shape
            if (mxUtils.indexOf(connectStyles, keys[i]) >= 0) {
              if (edge || mxUtils.indexOf(alwaysEdgeStyles, keys[i]) >= 0) {
                currentEdgeStyle[keys[i]] = values[i];
              }
            } else if (mxUtils.indexOf(stylesArr, keys[i]) >= 0) {
              if (vertex || common) {
                currentStyle[keys[i]] = values[i];
              }

              if (edge || common || mxUtils.indexOf(alwaysEdgeStyles, keys[i]) >= 0) {
                currentEdgeStyle[keys[i]] = values[i];
              }
            }
          }
        })
      );

      // Makes sure the current layer is visible when cells are added
      graph.addListener(mxEvent.CELLS_ADDED, function (sender, evt) {
        const cells = evt.getProperty('cells');
        const parent = evt.getProperty('parent');

        if (
          graph.getModel().isLayer(parent) &&
          !graph.isCellVisible(parent) &&
          cells != null &&
          cells.length > 0
        ) {
          graph.getModel().setVisible(parent, true);
        }
      });

      // Updates the editor UI after the window has been resized or the orientation changes
      // Timeout is workaround for old IE versions which have a delay for DOM client sizes.
      // Should not use delay > 0 to avoid handle multiple repaints during window resize
      mxEvent.addListener(
        window,
        'resize',
        mxUtils.bind(this, function () {
          this.refresh();
        })
      );

      mxEvent.addListener(
        window,
        'orientationchange',
        mxUtils.bind(this, function () {
          this.refresh();
        })
      );

      // Workaround for bug on iOS see

      //noinspection TypeScriptUnresolvedVariable
      if (mxClient.IS_IOS && !window.navigator['standalone']) {
        mxEvent.addListener(
          window,
          'scroll',
          mxUtils.bind(this, function () {
            window.scrollTo(0, 0);
          })
        );
      }

      /**
       * Sets the initial scrollbar locations after a file was loaded.
       */
      this.editor.addListener(
        'resetGraphView',
        mxUtils.bind(this, function () {
          // Timeout is a workaround for delay needed in older browsers and IE
          this.resetScrollbars();
        })
      );

      // Resets UI, updates action and menu states
      this.editor.resetGraph();
      this.init();
    };

    mxEdgeStyle.orthBuffer = 30;

    // Extends mxEventSource
    mxUtils.extend(EditorUI, mxEventSource);

    /**
     * Specifies if animations are allowed in <executeLayout>. Default is true.
     */
    EditorUI.prototype.allowAnimation = true;

    /**
     * Installs the listeners to update the action states.
     */
    EditorUI.prototype.init = function () {
      this.editor.graph.getSelectionModel().addListener(
        mxEvent.CHANGE,
        mxUtils.bind(this, function (selectionModel) {
          sidebarSvc.refresh();
        })
      );

      this.initCanvas();
      const that = this;

      // POST INITIALIZAITON taken from ui-graph.
      setTimeout(function () {
        mxEvent.addListener(
          that.editor.graph.container,
          'keypress',
          mxUtils.bind(this, function (evt) {
            // KNOWN: Focus does not work if label is empty in quirks mode
            if (
              !that.editor.graph.isEditing() &&
              !that.editor.graph.isSelectionEmpty() &&
              evt.which !== 0 &&
              !mxEvent.isAltDown(evt) &&
              !mxEvent.isControlDown(evt) &&
              !mxEvent.isMetaDown(evt)
            ) {
              that.editor.graph.escape();
              that.editor.graph.startEditing();

              if (mxClient.IS_FF) {
                const ce = that.editor.graph.cellEditor;

                // Initial keystroke is lost in FF
                if (ce.textarea.style.display != 'none') {
                  ce.textarea.value = String.fromCharCode(evt.which);
                } else if (ce.text2 != null) {
                  ce.text2.innerHTML = String.fromCharCode(evt.which);
                  const range = document.createRange();

                  range.selectNodeContents(ce.text2);
                  range.collapse(false);

                  const selection = window.getSelection();

                  selection.removeAllRanges();
                  selection.addRange(range);
                }
              }
            }
          })
        );
      }, 4);

      // Required for calling edit link action below
      const ui = this;

      const connectHandle = new mxImage(
        mxClient.IS_SVG
          ? // eslint-disable-next-line max-len
            'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABoAAAAaCAYAAACpSkzOAAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAABLZJREFUeNqcVl1MHFUUPrPMsn8s7UItthFa/ImNRB7aIC0aWyQhpqZWTBMNkQc3PvjiQ4MR05honyQmpjE8mmw01EqMwQcIopYAdUNR0CYYsk37oMIGuru1rEt2Z3Zm7lzPnbl3dlgXqE5ysnfPved85++ecyW4v0/aaZPit6OwJO2oQOLkcf16XKBMuVlGFUG3AxK8Kk5eJLm/v39vNBo9UVtb20gIUTc2NpJDQ0MLsVhsE/d1JAOJcNoCyIAqgTCrq5GCSHVra2vvFQqFON3my+fzs8lk8jyejXAZr/CcYVWKqhskPDExcRyVXBcKVYPSpb8pnUqZNH7XpIkcpRopAaIx06Ojo0dRtobrcMDKk8w2ZCTf1NTUsc7Ozgl0ObCuAHyxSuD7eyYopmRLUFuyFiVO7/PAqw95oN6HMSMkPTs7e66rq+sGniiycDIj3DmSeD58k5OTrd3d3TO4Wf1j2oSLt3RQTVuz2yoMisPZI0tw8XEZjtV7GFhubGzsuZ6engRuaTxnVHJ5w2IbUhTlB7/ff3R6ncCFhMbNt21xwiAJ21gO7KWMzI+e8EJHgwwYxmuhUOgl3ClwMOouXW8qlXqbgfy5acIHSwUwNQOJcDKA6sSiEs+0/xcJaKoB7y+pkFFMCAaDz2KBRHkqqoQnjkdYul3sTyyhQL5ogIHKjSInzaafng9bZGg6JwOIbtM9RYfLN1VLYSQS6eFRsu6jAzQ8PNyE3jyVK5owvlqwFTDluuGsGYmPuHhu+ur3PCgGZV49PTAwEBHOOEAdHR0n2GIppYGKlglPSJEDqvZ/8elq6YybNhUDbv+lWWf6+vraBIYsXKupqTnEGGs5DRUTdrWtG81yfTPa+K9Lt/zaQWd9JLbqrhm4kzOgtcEH4XC4SVwhWRw2TdMyQ0YBDa21SouVlCTt2nE17qm4Yh6OilVKxBmZ71Es6xRj1PmlLSFim02Xbjnmrpx/zFo1XbpdKn1hFD+zL2BnJJvNJsUh4ZE5Pz8/19zcDK0HsV1h6HQmSLcfFHpRdzqEsIjh7ZE9cKQhYLFGRkYWRVcXxWD29vauoFcLdaEqONMYwEpDMCwCh4o2lYC28tmahfDlh4Pg90qAuuKDg4MbAsjxiMliE70WCATa3jq1H75ObGBvo+7hZrWc2gu/Wl5QUSxO05QgjK3ozZP7Rdi+46ODlrcg1nFDqqpO+3y+J6/E78Ab36zwyiv1OgolxaLfCXO+fOUwnG17gLWgeWxBLyArz2eV6XHlnKGry8vL7zJG7zMPwqdnG8FDsGtjGBVsNQWd/ZoWWWvkFzBkfkLg8rlDFghTuri4+A7v3kQUQ8UxgYVxsr29fYwxf/sjBx+PrcDniayrgG1RP3r7eute6H/xMDxyIGhxZ2ZmunDELHAgXYyJ8sFnjQo2+NCq0xjGhBhsa3dV+u1Cmn52dZVemU7SqzcyNJ0tOoMPk/9LPB4/xWS5ji2Dr9KUrRL5QqrPZDKfYMwXtxvlCPBzOp3+kJ3lMtWVRrm0y+tH5h3YOz4+/mhLS8txrMoDKKwh+Prc3Nx17GcrPOE6z4nJ3yb0/zy3PNxTaZfnFv0vz62dHo/l5+n9PCIZ0D8CDACEWhv+nM/wTwAAAABJRU5ErkJggg=='
          : IMAGE_PATH + '/handle-connect.png',
        26,
        26
      );

      const touchStyle =
        mxClient.IS_TOUCH ||
        (mxClient.IS_FF && mxClient.IS_WIN) ||
        navigator.maxTouchPoints > 0 ||
        // @ts-ignore
        navigator.msMaxTouchPoints > 0;

      /**
       * Implements touch style
       */
      if (touchStyle) {
        // Larger tolerance for real touch devices
        // @ts-ignore
        if (mxClient.IS_TOUCH || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0) {
          mxShape.prototype.svgStrokeTolerance = 18;
          mxVertexHandler.prototype.tolerance = 12;
          mxEdgeHandler.prototype.tolerance = 12;

          mxVertexHandler.prototype.rotationHandleVSpacing = -24;

          // Implements a smaller tolerance for mouse events and a larger tolerance for touch
          // events on touch devices. The default tolerance (4px) is used for mouse events.
          mxConstraintHandler.prototype.getTolerance = function (me) {
            return mxEvent.isMouseEvent(me.getEvent()) ? 4 : this.graph.getTolerance();
          };
        }

        // One finger pans (no rubberband selection) must start regardless of mouse button
        mxPanningHandler.prototype.isPanningTrigger = function (me) {
          const evt = me.getEvent();

          return (
            (me.getState() == null && !mxEvent.isMouseEvent(evt)) ||
            (mxEvent.isPopupTrigger(evt) && (me.getState() == null || mxEvent.isShiftDown(evt)))
          );
        };

        // Don't clear selection if multiple cells selected
        const graphHandlerMouseDown = mxGraphHandler.prototype.mouseDown;

        mxGraphHandler.prototype.mouseDown = function (sender, me) {
          graphHandlerMouseDown.apply(this, arguments);

          if (
            mxEvent.isTouchEvent(me.getEvent()) &&
            this.graph.isCellSelected(me.getCell()) &&
            this.graph.getSelectionCount() > 1
          ) {
            this.delayedSelection = false;
          }
        };
      }

      mxGraphModel.prototype.maintainEdgeParent = false;

      const vertexHandlerMouseMove = mxVertexHandler.prototype.mouseMove;

      mxVertexHandler.prototype.mouseMove = function () {
        vertexHandlerMouseMove.apply(this, arguments);

        if (this.graph.graphHandler.first != null) {
          if (this.linkHint != null) {
            this.linkHint.style.display = 'none';
          }

          if (this.connectorImg != null) {
            this.connectorImg.style.display = 'none';
          }

          if (this.rotationShape != null && this.rotationShape.node != null) {
            this.rotationShape.node.style.display = 'none';
          }
        }
      };

      const vertexHandlerMouseUp = mxVertexHandler.prototype.mouseUp;

      mxVertexHandler.prototype.mouseUp = function () {
        vertexHandlerMouseUp.apply(this, arguments);

        if (this.connectorImg != null) {
          this.connectorImg.style.display = this.graph.getSelectionCount() == 1 ? '' : 'none';
        }

        if (this.linkHint != null) {
          this.linkHint.style.display = this.graph.getSelectionCount() == 1 ? '' : 'none';
        }

        // Shows rotation handle only if one vertex is selected
        if (this.rotationShape != null && this.rotationShape.node != null) {
          this.rotationShape.node.style.display = this.graph.getSelectionCount() == 1 ? '' : 'none';
        }
      };

      const vertexHandlerReset = mxVertexHandler.prototype.reset;

      mxVertexHandler.prototype.reset = function () {
        vertexHandlerReset.apply(this, arguments);

        // Shows rotation handle only if one vertex is selected
        if (this.rotationShape != null && this.rotationShape.node != null) {
          this.rotationShape.node.style.display = this.graph.getSelectionCount() == 1 ? '' : 'none';
        }
      };

      const vertexHandlerInit = mxVertexHandler.prototype.init;

      mxVertexHandler.prototype.init = function () {
        this.state.cell.oldValue = undefined;
        vertexHandlerInit.apply(this, arguments);

        let redraw = false;

        if (this.rotationShape != null) {
          this.rotationShape.node.setAttribute('title', mxResources.get('rotateTooltip'));
        }

        const update = mxUtils.bind(this, function () {
          const singleSelection = this.graph.getSelectionCount() === 1;

          if (this.connectorImg != null) {
            this.connectorImg.style.display = singleSelection ? '' : 'none';
          }

          if (this.linkHint != null) {
            this.linkHint.style.display = singleSelection ? '' : 'none';
          }

          // Shows rotation handle only if one vertex is selected
          if (this.rotationShape != null && this.rotationShape.node != null) {
            this.rotationShape.node.style.display = singleSelection ? '' : 'none';
          }

          if (this.specialHandle != null) {
            this.specialHandle.node.style.display =
              this.graph.isEnabled() &&
              this.graph.getSelectionCount() < this.graph.graphHandler.maxCells
                ? ''
                : 'none';
          }

          singleSelection && this.redraw();
        });

        this.selectionHandler = mxUtils.bind(this, function (sender, evt) {
          update();
        });

        this.graph.getSelectionModel().addListener(mxEvent.CHANGE, this.selectionHandler);

        this.changeHandler = mxUtils.bind(this, function (sender, evt) {
          this.updateLinkHint(this.graph.getLinkForCell(this.state.cell));
          update();
        });

        this.graph.getModel().addListener(mxEvent.CHANGE, this.changeHandler);

        if (this.graph.isEnabled()) {
          // Only show connector image on one cell and do not show on containers
          if (this.graph.isCellConnectable(this.state.cell)) {
            // Workaround for event redirection via image tag in quirks and IE8
            if (mxClient.IS_IE && !mxClient.IS_SVG) {
              // Workaround for PNG images in IE6
              if (mxClient.IS_IE6 && document.compatMode != 'CSS1Compat') {
                this.connectorImg = document.createElement(mxClient.VML_PREFIX + ':image');
                this.connectorImg.setAttribute('src', connectHandle.src);
                this.connectorImg.style.borderStyle = 'none';
              } else {
                this.connectorImg = document.createElement('div');
                this.connectorImg.style.backgroundImage = 'url(' + connectHandle.src + ')';
                this.connectorImg.style.backgroundPosition = 'center';
                this.connectorImg.style.backgroundRepeat = 'no-repeat';
              }

              this.connectorImg.style.width = connectHandle.width + 4 + 'px';
              this.connectorImg.style.height = connectHandle.height + 4 + 'px';
              this.connectorImg.style.display = mxClient.IS_QUIRKS ? 'inline' : 'inline-block';
            } else {
              this.connectorImg = mxUtils.createImage(connectHandle.src);

              if (touchStyle) {
                this.connectorImg.style.width = '29px';
                this.connectorImg.style.height = '29px';
              } else {
                this.connectorImg.style.width = connectHandle.width + 'px';
                this.connectorImg.style.height = connectHandle.height + 'px';
              }
            }

            this.connectorImg.style.cursor = 'crosshair';
            this.connectorImg.style.position = 'absolute';
            this.connectorImg.setAttribute('title', mxResources.get('plusTooltip'));

            if (
              // @ts-ignore
              !(mxClient.IS_TOUCH || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0)
            ) {
              mxEvent.redirectMouseEvents(this.connectorImg, this.graph, this.state);
            }

            let mousePoint = null;

            // Starts connecting on touch/mouse down
            mxEvent.addGestureListeners(
              this.connectorImg,
              mxUtils.bind(this, function (evt) {
                if (!mxEvent.isAltDown(evt) && !mxEvent.isPopupTrigger(evt)) {
                  this.graph.popupMenuHandler.hideMenu();
                  this.graph.stopEditing(false);

                  mousePoint = mxUtils.convertPoint(
                    this.graph.container,
                    mxEvent.getClientX(evt),
                    mxEvent.getClientY(evt)
                  );
                  this.graph.connectionHandler.start(this.state, mousePoint.x, mousePoint.y);
                  this.graph.isMouseTrigger = mxEvent.isMouseEvent(evt);
                  this.graph.isMouseDown = true;

                  mxEvent.consume(evt);
                }
              }),
              null,
              mxUtils.bind(this, function (evt) {
                if (mousePoint != null) {
                  const pt = mxUtils.convertPoint(
                    this.graph.container,
                    mxEvent.getClientX(evt),
                    mxEvent.getClientY(evt)
                  );
                  const tol = this.graph.tolerance;

                  mousePoint = null;
                }
              })
            );

            this.graph.container.appendChild(this.connectorImg);
            redraw = true;
          }
        }

        const link = this.graph.getLinkForCell(this.state.cell);

        this.updateLinkHint(link);

        if (link != null) {
          redraw = true;
        }

        if (redraw) {
          this.redraw();
        }
      };

      function createHint() {
        const hint = document.createElement('div');

        hint.className = 'geHint';
        hint.style.whiteSpace = 'nowrap';
        hint.style.position = 'absolute';

        return hint;
      }

      mxVertexHandler.prototype.updateLinkHint = function (link) {
        if (link == null) {
          if (this.linkHint != null) {
            this.linkHint.parentNode.removeChild(this.linkHint);
            this.linkHint = null;
          }
        } else if (link != null) {
          if (this.linkHint == null) {
            this.linkHint = createHint();
            this.linkHint.style.padding = '4px 10px 6px 10px';
            this.linkHint.style.fontSize = '90%';
            this.linkHint.style.opacity = '1';
            this.linkHint.style.filter = '';
            this.updateLinkHint(link);

            this.graph.container.appendChild(this.linkHint);
          }

          let label = link;
          const max = 60;
          const head = 36;
          const tail = 20;

          if (label.length > max) {
            label = label.substring(0, head) + '...' + label.substring(label.length - tail);
          }

          const a = document.createElement('a');

          a.setAttribute('href', link);
          a.setAttribute('title', link);

          if (this.graph.linkTarget != null) {
            a.setAttribute('target', this.graph.linkTarget);
          }

          mxUtils.write(a, label);

          this.linkHint.innerHTML = '';
          this.linkHint.appendChild(a);

          if (this.graph.isEnabled()) {
            const changeLink = document.createElement('img');

            changeLink.setAttribute('src', IMAGE_PATH + '/edit.gif');
            changeLink.setAttribute('title', mxResources.get('editLink'));
            changeLink.setAttribute('width', '11');
            changeLink.setAttribute('height', '11');
            changeLink.style.marginLeft = '10px';
            changeLink.style.marginBottom = '-1px';
            changeLink.style.cursor = 'pointer';
            this.linkHint.appendChild(changeLink);

            mxEvent.addListener(
              changeLink,
              'click',
              mxUtils.bind(this, function (evt) {
                this.graph.setSelectionCell(this.state.cell);
                ui.actions.get('editLink').funct();
                mxEvent.consume(evt);
              })
            );
          }
        }
      };

      mxEdgeHandler.prototype.updateLinkHint = mxVertexHandler.prototype.updateLinkHint;

      const edgeHandlerInit = mxEdgeHandler.prototype.init;

      mxEdgeHandler.prototype.init = function () {
        edgeHandlerInit.apply(this, arguments);

        // Disables connection points
        this.constraintHandler.isEnabled = mxUtils.bind(this, function () {
          return this.state.view.graph.connectionHandler.isEnabled();
        });

        const update = mxUtils.bind(this, function () {
          if (this.linkHint != null) {
            this.linkHint.style.display = this.graph.getSelectionCount() == 1 ? '' : 'none';
          }

          if (this.labelShape != null) {
            this.labelShape.node.style.display =
              this.graph.isEnabled() &&
              this.graph.getSelectionCount() < this.graph.graphHandler.maxCells
                ? ''
                : 'none';
          }
        });

        this.selectionHandler = mxUtils.bind(this, function (sender, evt) {
          update();
        });

        this.graph.getSelectionModel().addListener(mxEvent.CHANGE, this.selectionHandler);

        this.changeHandler = mxUtils.bind(this, function (sender, evt) {
          this.updateLinkHint(this.graph.getLinkForCell(this.state.cell));
          update();
          this.redrawHandles();
        });

        this.graph.getModel().addListener(mxEvent.CHANGE, this.changeHandler);

        const link = this.graph.getLinkForCell(this.state.cell);

        if (link != null) {
          this.updateLinkHint(link);
          this.redrawHandles();
        }
      };
    };

    /**
     * Private helper method.
     */
    EditorUI.prototype.getCssClassForMarker = function (prefix, shape, marker, fill) {
      let result = '';

      if (shape == 'flexArrow') {
        result =
          marker != null && marker != mxConstants.NONE
            ? 'geSprite geSprite-' + prefix + 'blocktrans'
            : 'geSprite geSprite-noarrow';
      } else {
        if (marker == mxConstants.ARROW_CLASSIC) {
          result =
            fill == '1'
              ? 'geSprite geSprite-' + prefix + 'classic'
              : 'geSprite geSprite-' + prefix + 'classictrans';
        } else if (marker == mxConstants.ARROW_OPEN) {
          result = 'geSprite geSprite-' + prefix + 'open';
        } else if (marker == mxConstants.ARROW_BLOCK) {
          result =
            fill == '1'
              ? 'geSprite geSprite-' + prefix + 'block'
              : 'geSprite geSprite-' + prefix + 'blocktrans';
        } else if (marker == mxConstants.ARROW_OVAL) {
          result =
            fill == '1'
              ? 'geSprite geSprite-' + prefix + 'oval'
              : 'geSprite geSprite-' + prefix + 'ovaltrans';
        } else if (marker == mxConstants.ARROW_DIAMOND) {
          result =
            fill == '1'
              ? 'geSprite geSprite-' + prefix + 'diamond'
              : 'geSprite geSprite-' + prefix + 'diamondtrans';
        } else if (marker == mxConstants.ARROW_DIAMOND_THIN) {
          result =
            fill == '1'
              ? 'geSprite geSprite-' + prefix + 'thindiamond'
              : 'geSprite geSprite-' + prefix + 'thindiamondtrans';
        } else {
          result = 'geSprite geSprite-noarrow';
        }
      }

      return result;
    };

    /**
     * Initializes the infinite canvas.
     */
    EditorUI.prototype.initCanvas = function () {
      // Initial page layout view, scrollBuffer and timer-based scrolling
      const graph = this.editor.graph;

      graph.timerAutoScroll = true;

      /**
       * Specifies the size of the size for "tiles" to be used for a graph with
       * scrollbars but no visible background page. A good value is large
       * enough to reduce the number of repaints that is caused for auto-
       * translation, which depends on this value, and small enough to give
       * a small empty buffer around the graph. Default is 400x400.
       */
      graph.scrollTileSize = new mxRectangle(0, 0, 400, 400);

      /**
       * Returns the padding for pages in page view with scrollbars.
       */
      graph.getPagePadding = function () {
        return new mxPoint(
          Math.max(0, Math.round(graph.container.offsetWidth - 34)),
          Math.max(0, Math.round(graph.container.offsetHeight - 34))
        );
      };

      /**
       * Returns the size of the page format scaled with the page size.
       */
      graph.getPageSize = function () {
        return this.pageVisible
          ? new mxRectangle(
              0,
              0,
              this.pageFormat.width * this.pageScale,
              this.pageFormat.height * this.pageScale
            )
          : this.scrollTileSize;
      };

      /**
       * Returns a rectangle describing the position and count of the
       * background pages, where x and y are the position of the top,
       * left page and width and height are the vertical and horizontal
       * page count.
       */
      graph.getPageLayout = function () {
        const size = this.pageVisible ? this.getPageSize() : this.scrollTileSize;
        const bounds = this.getGraphBounds();

        if (bounds.width == 0 || bounds.height == 0) {
          return new mxRectangle(0, 0, 1, 1);
        } else {
          // Computes untransformed graph bounds
          const x = Math.ceil(bounds.x / this.view.scale - this.view.translate.x);
          const y = Math.ceil(bounds.y / this.view.scale - this.view.translate.y);
          const w = Math.floor(bounds.width / this.view.scale);
          const h = Math.floor(bounds.height / this.view.scale);

          const x0 = Math.floor(x / size.width);
          const y0 = Math.floor(y / size.height);
          const w0 = Math.ceil((x + w) / size.width) - x0;
          const h0 = Math.ceil((y + h) / size.height) - y0;

          return new mxRectangle(x0, y0, w0, h0);
        }
      };

      // Fits the number of background pages to the graph
      graph.view.getBackgroundPageBounds = function () {
        const layout = this.graph.getPageLayout();
        const page = this.graph.getPageSize();

        return new mxRectangle(
          this.scale * (this.translate.x + layout.x * page.width),
          this.scale * (this.translate.y + layout.y * page.height),
          this.scale * layout.width * page.width,
          this.scale * layout.height * page.height
        );
      };

      // Scales pages/graph to fit available size
      let resize = null;

      if (this.editor.chromeless) {
        resize = mxUtils.bind(this, function (autoscale) {
          if (graph.container != null) {
            const b = graph.pageVisible
              ? graph.view.getBackgroundPageBounds()
              : graph.getGraphBounds();
            const tr = graph.view.translate;
            const s = graph.view.scale;

            // Normalizes the bounds
            b.x = b.x / s - tr.x;
            b.y = b.y / s - tr.y;
            b.width /= s;
            b.height /= s;

            const st = graph.container.scrollTop;
            const sl = graph.container.scrollLeft;
            //noinspection TypeScriptUnresolvedVariable
            let sb = mxClient.IS_QUIRKS || document['documentMode'] >= 8 ? 20 : 14;

            //noinspection TypeScriptUnresolvedVariable
            if (document['documentMode'] == 8 || document['documentMode'] == 9) {
              sb += 3;
            }

            const cw = graph.container.offsetWidth - sb;
            const ch = graph.container.offsetHeight - sb;

            const ns = autoscale ? Math.max(0.3, Math.min(1, cw / b.width)) : s;
            const dx = Math.max((cw - ns * b.width) / 2, 0) / ns;
            const dy = Math.max((ch - ns * b.height) / 4, 0) / ns;

            graph.view.scaleAndTranslate(ns, dx - b.x, dy - b.y);

            graph.container.scrollTop = (st * ns) / s;
            graph.container.scrollLeft = (sl * ns) / s;
          }
        });

        mxEvent.addListener(
          window,
          'resize',
          mxUtils.bind(this, function () {
            resize(false);
          })
        );

        this.editor.addListener(
          'resetGraphView',
          mxUtils.bind(this, function () {
            resize(true);
          })
        );

        // Workaround for clipping problem
        graph.getPreferredPageSize = function (bounds, width, height) {
          const pages = this.getPageLayout();
          const size = this.getPageSize();
          const s = this.view.scale;

          return new mxRectangle(
            0,
            0,
            pages.width * size.width * s,
            pages.height * size.height * s
          );
        };
      } else if (this.editor.extendCanvas) {
        graph.getPreferredPageSize = function (bounds, width, height) {
          const pages = this.getPageLayout();
          const size = this.getPageSize();

          return new mxRectangle(0, 0, pages.width * size.width, pages.height * size.height);
        };

        /**
         * Guesses autoTranslate to avoid another repaint (see below).
         * Works if only the scale of the graph changes or if pages
         * are visible and the visible pages do not change.
         */
        const graphViewValidate = graph.view.validate;

        graph.view.validate = function () {
          if (this.graph.container != null && mxUtils.hasScrollbars(this.graph.container)) {
            const pad = this.graph.getPagePadding();
            const size = this.graph.getPageSize();

            // Updating scrollbars here causes flickering in quirks and is not needed
            // if zoom method is always used to set the current scale on the graph.
            const tx = this.translate.x;
            const ty = this.translate.y;

            this.translate.x = pad.x / this.scale - (this.x0 || 0) * size.width;
            this.translate.y = pad.y / this.scale - (this.y0 || 0) * size.height;
          }

          graphViewValidate.apply(this, arguments);
        };

        const graphSizeDidChange = graph.sizeDidChange;

        graph.sizeDidChange = function () {
          if (this.container != null && mxUtils.hasScrollbars(this.container)) {
            const pages = this.getPageLayout();
            const pad = this.getPagePadding();
            const size = this.getPageSize();

            // Updates the minimum graph size
            const minw = Math.ceil((2 * pad.x) / this.view.scale + pages.width * size.width);
            const minh = Math.ceil((2 * pad.y) / this.view.scale + pages.height * size.height);

            const min = graph.minimumGraphSize;

            // LATER: Fix flicker of scrollbar size in IE quirks mode
            // after delayed call in window.resize event handler
            if (min == null || min.width != minw || min.height != minh) {
              graph.minimumGraphSize = new mxRectangle(0, 0, minw, minh);
            }

            // Updates auto-translate to include padding and graph size
            const dx = pad.x / this.view.scale - pages.x * size.width;
            const dy = pad.y / this.view.scale - pages.y * size.height;

            if (
              !this.autoTranslate &&
              (this.view.translate.x != dx || this.view.translate.y != dy)
            ) {
              this.autoTranslate = true;
              this.view.x0 = pages.x;
              this.view.y0 = pages.y;

              // NOTE: THIS INVOKES THIS METHOD AGAIN. UNFORTUNATELY THERE IS NO WAY AROUND THIS SINCE THE
              // BOUNDS ARE KNOWN AFTER THE VALIDATION AND SETTING THE TRANSLATE TRIGGERS A REVALIDATION.
              // SHOULD MOVE TRANSLATE/SCALE TO VIEW.
              const tx = graph.view.translate.x;
              const ty = graph.view.translate.y;

              graph.view.setTranslate(dx, dy);
              graph.container.scrollLeft += (dx - tx) * graph.view.scale;
              graph.container.scrollTop += (dy - ty) * graph.view.scale;

              this.autoTranslate = false;

              return;
            }

            graphSizeDidChange.apply(this, arguments);
          }
        };
      }

      mxEvent.addMouseWheelListener(
        mxUtils.bind(this, function (evt, up) {
          if (mxEvent.isAltDown(evt) || graph.panningHandler.isActive()) {
            if (this.dialogs == null || this.dialogs.length == 0) {
              if (up) {
                graph.zoomIn();
              } else {
                graph.zoomOut();
              }

              if (resize != null) {
                resize(false);
              }
            }

            mxEvent.consume(evt);
          }
        })
      );
    };

    /**
     * Hook for allowing selection and context menu for certain events.
     */
    EditorUI.prototype.isSelectionAllowed = function (evt) {
      return false;
      //return mxEvent.getSource(evt).nodeName == 'SELECT' || (mxEvent.getSource(evt).nodeName == 'INPUT' &&
      //  mxUtils.isAncestorNode(this.formatContainer, mxEvent.getSource(evt)));
    };

    /**
     * Specifies if the graph has scrollbars.
     */
    EditorUI.prototype.setScrollbars = function (value) {
      const graph = this.editor.graph;
      const prev = graph.container.style.overflow;

      graph.scrollbars = value;
      this.editor.updateGraphComponents();

      if (prev != graph.container.style.overflow) {
        if (graph.container.style.overflow == 'hidden') {
          const t = graph.view.translate;

          graph.view.setTranslate(
            t.x - graph.container.scrollLeft / graph.view.scale,
            t.y - graph.container.scrollTop / graph.view.scale
          );
          graph.container.scrollLeft = 0;
          graph.container.scrollTop = 0;
          graph.minimumGraphSize = null;
          graph.sizeDidChange();
        } else {
          const dx = graph.view.translate.x;
          const dy = graph.view.translate.y;

          graph.view.translate.x = 0;
          graph.view.translate.y = 0;
          graph.sizeDidChange();
          graph.container.scrollLeft -= Math.round(dx * graph.view.scale);
          graph.container.scrollTop -= Math.round(dy * graph.view.scale);
        }
      }

      this.fireEvent(new mxEventObject('scrollbarsChanged'));
    };

    /**
     * Returns true if the graph has scrollbars.
     */
    EditorUI.prototype.hasScrollbars = function () {
      return this.editor.graph.scrollbars;
    };

    /**
     * Resets the state of the scrollbars.
     */
    EditorUI.prototype.resetScrollbars = function () {
      const graph = this.editor.graph;

      if (!this.editor.extendCanvas) {
        graph.container.scrollTop = 0;
        graph.container.scrollLeft = 0;

        if (!mxUtils.hasScrollbars(graph.container)) {
          graph.view.setTranslate(0, 0);
        }
      } else if (!this.editor.chromeless) {
        if (mxUtils.hasScrollbars(graph.container)) {
          if (graph.pageVisible) {
            const pad = graph.getPagePadding();

            graph.container.scrollTop = Math.floor(pad.y - this.editor.initialTopSpacing);
            graph.container.scrollLeft = Math.floor(
              Math.min(pad.x, (graph.container.scrollWidth - graph.container.clientWidth) / 2)
            );
          } else {
            const bounds = graph.getGraphBounds();
            const width = Math.max(bounds.width, graph.scrollTileSize.width * graph.view.scale);
            const height = Math.max(bounds.height, graph.scrollTileSize.height * graph.view.scale);

            graph.container.scrollTop = Math.floor(
              Math.max(
                0,
                bounds.y -
                  Math.max(
                    CANVAS_TOP_MARGIN(
                      diagramTypeConfigService.isArchitecture() && sfaHeaderSvc.isPinned
                    ),
                    (graph.container.clientHeight - height) / 4
                  )
              )
            );
            graph.container.scrollLeft = Math.floor(
              Math.max(0, bounds.x - Math.max(0, (graph.container.clientWidth - width) / 2))
            );
          }
        } else {
          // This code is not actively used since the default for scrollbars is always true
          if (graph.pageVisible) {
            const b = graph.view.getBackgroundPageBounds();

            graph.view.setTranslate(
              Math.floor(Math.max(0, (graph.container.clientWidth - b.width) / 2) - b.x),
              Math.floor(Math.max(0, (graph.container.clientHeight - b.height) / 2) - b.y)
            );
          } else {
            const bounds = graph.getGraphBounds();

            graph.view.setTranslate(
              Math.floor(
                Math.max(
                  0,
                  Math.max(0, (graph.container.clientWidth - bounds.width) / 2) - bounds.x
                )
              ),
              Math.floor(
                Math.max(0, Math.max(20, (graph.container.clientHeight - bounds.height) / 4)) -
                  bounds.y
              )
            );
          }
        }
      }
    };

    /**
     * Loads the stylesheet for this graph.
     */
    EditorUI.prototype.setBackgroundColor = function (value) {
      this.editor.graph.background = value;
      this.editor.updateGraphComponents();

      this.fireEvent(new mxEventObject('backgroundColorChanged'));
    };

    /**
     * Loads the stylesheet for this graph.
     */
    EditorUI.prototype.setFoldingEnabled = function (value) {
      this.editor.graph.foldingEnabled = value;
      this.editor.graph.view.revalidate();

      this.fireEvent(new mxEventObject('foldingEnabledChanged'));
    };

    let initRefresh = true;

    /**
     * Refreshes the viewport.
     */
    EditorUI.prototype.refresh = function () {
      //noinspection TypeScriptUnresolvedVariable
      const quirks =
        mxClient.IS_IE && (document['documentMode'] == null || document['documentMode'] == 5);
      let w = this.container.clientWidth;
      let h = this.container.clientHeight;

      if (this.container == document.body) {
        w = document.body.clientWidth || document.documentElement.clientWidth;
        h = quirks
          ? document.body.clientHeight || document.documentElement.clientHeight
          : document.documentElement.clientHeight;
      }

      // Workaround for bug on iOS see
      // http://stackoverflow.com/questions/19012135/ios-7-ipad-safari-landscape-innerheight-outerheight-layout-issue
      let off = 0;

      //noinspection TypeScriptUnresolvedVariable
      if (mxClient.IS_IOS && !window.navigator['standalone']) {
        if (window.innerHeight != document.documentElement.clientHeight) {
          off = document[0].documentElement.clientHeight - window.innerHeight;
          window.scrollTo(0, 0);
        }
      }

      const effHsplitPosition = Math.max(0, Math.min(this.hsplitPosition, w - this.splitSize - 20));

      const tmp = 0;

      if (initRefresh) {
        this.diagramContainer.style.left = '0';
        this.diagramContainer.style.top = '0';
        this.diagramContainer.style.paddingRight = '16px';
        this.diagramContainer.style.paddingBottom = '16px';
        initRefresh = false;
      }

      this.diagramContainer.style.right = 0;

      if (quirks) {
        this.diagramContainer.style.width =
          this.hsplit.parentNode != null
            ? Math.max(0, w - effHsplitPosition - this.splitSize) + 'px'
            : w + 'px'; // fw?
        const diagramHeight = Math.max(
          0,
          h - this.footerHeight - this.menubarHeight - this.toolbarHeight
        );

        this.diagramContainer.style.height = diagramHeight + 'px';
      } else {
        this.diagramContainer.style.bottom = '0px';
      }

      this.editor.graph.sizeDidChange();
    };

    /**
     * Creates the required containers.
     */
    EditorUI.prototype.createDivs = function () {
      this.diagramContainer = this.createDiv('geDiagramContainer');

      mxPopupMenu.prototype.popup = function (x, y, cell, evt, special) {
        if (this.div != null && this.tbody != null && this.factoryMethod != null) {
          this.div.style.left = x - 10 + 'px';
          this.div.style.top = y - 30 + 'px';

          // Removes all child nodes from the existing menu
          while (this.tbody.firstChild != null) {
            mxEvent.release(this.tbody.firstChild);
            this.tbody.removeChild(this.tbody.firstChild);
          }

          this.itemCount = 0;
          this.factoryMethod(this, cell, evt, special);

          if (this.itemCount > 0) {
            this.showMenu();
            this.fireEvent(new mxEventObject(mxEvent.SHOW));
          }
        }
      };

      const mxPopupMenuShowMenu = mxPopupMenu.prototype.showMenu;

      mxPopupMenu.prototype.showMenu = function () {
        mxPopupMenuShowMenu.apply(this, arguments);

        this.div.style.border = 'none';
        this.div.style['border-radius'] = '4px';
        this.div.style['box-shadow'] =
          '0 2px 8px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.3)';
        this.div.style['padding'] = '6px 0 5px';
        this.div.style['background'] = 'none';
        this.div.style['background-color'] = '#fff';
      };
    };

    /**
     * Creates the required containers.
     */
    EditorUI.prototype.createUi = function () {
      this.container.appendChild(this.diagramContainer);
    };

    /**
     * Creates the actual toolbar for the toolbar container.
     */
    EditorUI.prototype.createDiv = function (classname) {
      const elt = document.createElement('div');

      elt.className = classname;

      return elt;
    };

    /**
     * Executes the given layout.
     */
    EditorUI.prototype.executeLayout = function (exec, animate, post) {
      const graph = this.editor.graph;

      if (graph.isEnabled()) {
        graph.getModel().beginUpdate();

        try {
          exec();
        } finally {
          // Animates the changes in the graph model except
          // for Camino, where animation is too slow
          if (this.allowAnimation && animate && navigator.userAgent.indexOf('Camino') < 0) {
            // New API for animating graph layout results asynchronously
            const morph = new mxMorphing(graph);

            morph.addListener(
              mxEvent.DONE,
              mxUtils.bind(this, function () {
                graph.getModel().endUpdate();

                if (post != null) {
                  post();
                }
              })
            );

            morph.startAnimation();
          } else {
            graph.getModel().endUpdate();
          }
        }
      }
    };

    /**
     * Creates the keyboard event handler for the current graph and history.
     */
    EditorUI.prototype.createKeyHandler = function (editor) {
      const graph = this.editor.graph;
      const keyHandler = new mxKeyHandler(graph);

      const isEnabledForEvent = keyHandler.isEnabledForEvent;

      keyHandler.isEnabledForEvent = function (evt) {
        if (!this.graph.isEnabled() && this.isGraphEvent(evt)) {
          return evt.keyCode === 8 || evt.keyCode === 46;
        }

        return isEnabledForEvent.apply(this, arguments);
      };

      const isEventIgnored = keyHandler.isEventIgnored;

      keyHandler.isEventIgnored = function (evt) {
        // Handles undo via action
        return (
          ((!this.isControlDown(evt) || mxEvent.isShiftDown(evt) || evt.keyCode !== 90) &&
            isEventIgnored.apply(this, arguments)) ||
          graphHelperSvc.isCellEditing()
        );
      };

      // Routes command-key to control-key on Mac
      keyHandler.isControlDown = function (evt) {
        return mxClient.IS_MAC ? mxEvent.isMetaDown(evt) : mxEvent.isControlDown(evt);
      };

      // Helper function to move cells with the cursor keys
      function nudge(keyCode, stepSize?) {
        if (!graph.isSelectionEmpty() && graph.isEnabled()) {
          if (graph.getSelectionCells().length < 2) {
            stepSize = stepSize != null ? stepSize : 1;

            let dx = 0;
            let dy = 0;
            const cells = graph.getSelectionCells().filter(function (item) {
              return (
                (!item.edge || (item.edge && !item.target && !item.source)) &&
                !graph.isCellLocked(item)
              );
            });

            if (keyCode == 37) {
              dx = -stepSize;
            } else if (keyCode == 38) {
              dy = -stepSize;
            } else if (keyCode == 39) {
              dx = stepSize;
            } else if (keyCode == 40) {
              dy = stepSize;
            }

            graph.moveCells(cells, dx, dy);
            graph.scrollCellToVisible(graph.getSelectionCell());
          } else {
            // q9FlashMessages.show($filter('translate')("POPUPS.CURSOR_MOVING")); //@TODO: Implements toasters!
          }
        }
      }

      keyHandler.bindAction = mxUtils.bind(this, function (code, control, key, shift) {
        const action = this.actions.get(key);

        if (action != null) {
          const f = () => {
            if (action.isEnabled()) {
              action.funct();
            }
          };

          if (control) {
            if (shift) {
              keyHandler.bindControlShiftKey(code, f);
            } else {
              keyHandler.bindControlKey(code, f);
            }
          } else {
            if (shift) {
              keyHandler.bindShiftKey(code, f);
            } else {
              keyHandler.bindKey(code, f);
            }
          }
        }
      });

      const keyHandleEscape = keyHandler.escape;

      keyHandler.escape = function (evt) {
        keyHandleEscape.apply(this, arguments);
      };

      // Ignores enter keystroke. Remove this line if you want the
      // enter keystroke to stop editing.
      keyHandler.enter = function () {};

      keyHandler.bindControlKey(36, function () {
        graph.foldCells(true);
      }); // Ctrl+Home
      keyHandler.bindControlKey(35, function () {
        graph.foldCells(false);
      }); // Ctrl+End
      keyHandler.bindControlShiftKey(36, function () {
        graph.exitGroup();
      }); // Ctrl+Shift+Home
      keyHandler.bindControlShiftKey(35, function () {
        graph.enterGroup();
      }); // Ctrl+Shift+End
      keyHandler.bindKey(36, function () {
        graph.home();
      }); // Home
      keyHandler.bindKey(35, function () {
        graph.refresh();
      }); // End
      keyHandler.bindKey(37, function () {
        nudge(37);
      }); // Left arrow
      keyHandler.bindKey(38, function () {
        nudge(38);
      }); // Up arrow
      keyHandler.bindKey(39, function () {
        nudge(39);
      }); // Right arrow
      keyHandler.bindKey(40, function () {
        nudge(40);
      }); // Down arrow
      keyHandler.bindShiftKey(37, function () {
        nudge(37, graph.gridSize);
      }); // Shift+Left arrow
      keyHandler.bindShiftKey(38, function () {
        nudge(38, graph.gridSize);
      }); // Shift+Up arrow
      keyHandler.bindShiftKey(39, function () {
        nudge(39, graph.gridSize);
      }); // Shift+Right arrow
      keyHandler.bindShiftKey(40, function () {
        nudge(40, graph.gridSize);
      }); // Shift+Down arrow
      keyHandler.bindKey(9, function () {
        graph.selectNextCell();
      }); // Tab
      keyHandler.bindShiftKey(9, function () {
        graph.selectPreviousCell();
      }); // Shift+Tab
      keyHandler.bindControlKey(9, function () {
        graph.selectParentCell();
      }); // Ctrl++Tab
      keyHandler.bindControlShiftKey(9, function () {
        graph.selectChildCell();
      }); // Ctrl+Shift+Tab
      keyHandler.bindAction(8, false, 'delete'); // Backspace
      keyHandler.bindAction(46, false, 'delete'); // Delete
      keyHandler.bindAction(107, true, 'zoomIn'); // Ctrl+Plus
      keyHandler.bindAction(109, true, 'zoomOut'); // Ctrl+Minus
      keyHandler.bindAction(65, true, 'selectAll'); // Ctrl+A
      keyHandler.bindAction(65, true, 'selectVertices', true); // Ctrl+Shift+A
      keyHandler.bindAction(null, true, 'selectBoxes', true);
      keyHandler.bindAction(76, true, 'lockUnlock'); // Ctrl+L
      keyHandler.bindAction(80, true, 'print'); // Ctrl+P // absent in old DE?
      keyHandler.bindKey(113, function () {
        graph.startEditingAtCell();
      }); // F2
      keyHandler.bindControlKey(90, () => undoSvc.undo()); // Ctrl+Z

      if (mxClient.IS_MAC) {
        keyHandler.bindControlShiftKey(90, () => undoSvc.redo()); // Ctrl+Shift+Z
      } else {
        keyHandler.bindControlKey(89, () => undoSvc.redo()); // Ctrl+Y
      }

      keyHandler.bindAction(67, true, 'copy'); // Ctrl+C
      keyHandler.bindAction(86, true, 'paste'); // Ctrl+V

      return keyHandler;
    };

    EditorUI.prototype.destroy = function () {
      if (this.editor != null) {
        this.editor.destroy();
        this.editor = null;
      }

      if (this.menubar != null) {
        this.menubar.destroy();
        this.menubar = null;
      }

      if (this.sidebar != null) {
        this.sidebar.destroy();
        this.sidebar = null;
      }

      if (this.keyHandler != null) {
        this.keyHandler.destroy();
        this.keyHandler = null;
      }

      if (this.keydownHandler != null) {
        mxEvent.removeListener(document, 'keydown', this.keydownHandler);
        this.keydownHandler = null;
      }

      if (this.keyupHandler != null) {
        mxEvent.removeListener(document, 'keyup', this.keyupHandler);
        this.keyupHandler = null;
      }

      if (this.resizeHandler != null) {
        mxEvent.removeListener(window, 'resize', this.resizeHandler);
        this.resizeHandler = null;
      }

      if (this.gestureHandler != null) {
        mxEvent.removeGestureListeners(document, this.gestureHandler);
        this.gestureHandler = null;
      }

      if (this.orientationChangeHandler != null) {
        mxEvent.removeListener(window, 'orientationchange', this.orientationChangeHandler);
        this.orientationChangeHandler = null;
      }

      if (this.scrollHandler != null) {
        mxEvent.removeListener(window, 'scroll', this.scrollHandler);
        this.scrollHandler = null;
      }

      if (this.destroyFunctions != null) {
        for (let i = 0; i < this.destroyFunctions.length; i++) {
          this.destroyFunctions[i]();
        }

        this.destroyFunctions = null;
      }

      const c = [
        this.menubarContainer,
        this.sidebarContainer,
        this.formatContainer,
        this.diagramContainer,
        this.footerContainer,
        this.chromelessToolbar,
        this.hsplit,
        this.sidebarFooterContainer,
        this.layersDialog
      ];

      for (let i = 0; i < c.length; i++) {
        if (c[i] != null && c[i].parentNode != null) {
          c[i].parentNode.removeChild(c[i]);
        }
      }
    };

    this.editorUI = EditorUI;
  }
}
